import * as signalR from "@microsoft/signalr";

const STOP_GRACE_MS = 750;

const sharedState = {
  userId: null,
  connection: null,
  startPromise: null,
  consumers: 0,
  stopTimer: null,
  accessTokenFactory: null,
};

const clearPendingStop = () => {
  if (sharedState.stopTimer) {
    clearTimeout(sharedState.stopTimer);
    sharedState.stopTimer = null;
  }
};

const resetSharedState = () => {
  sharedState.userId = null;
  sharedState.connection = null;
  sharedState.startPromise = null;
  sharedState.consumers = 0;
  sharedState.stopTimer = null;
  sharedState.accessTokenFactory = null;
};

const stopCurrentConnectionImmediately = () => {
  clearPendingStop();
  const connection = sharedState.connection;
  resetSharedState();

  if (connection) {
    connection.stop().catch(() => {});
  }
};

// VIVA-1173 / VIVA-1260: la URL del hub ya no lleva `?userId=`. Ese parametro lo
// ponia el cliente y el servidor lo usaba para resolver el identificador de la
// conexion por encima del claim del JWT, que es el dato que si esta verificado. El
// identificador sale del token que viaja en `accessTokenFactory`: se midio en pre
// que el claim `nameidentifier` trae exactamente el mismo GUID que se mandaba aqui,
// asi que quitarlo no cambia a que grupo entra la conexion.
//
// OJO con el alcance: quitarlo de aqui NO corrige la vulnerabilidad —cualquier
// cliente puede volver a agregar el parametro a mano—, la correccion es de servidor
// (VIVA-1260). Esto es limpieza del lado cliente para dejar de mandar un dato que el
// servidor no debe mirar.
//
// `userId` se conserva en `sharedState` porque decide cuando tirar y rehacer la
// conexion al cambiar de cuenta en el mismo navegador, no para armar la URL.
const createConnection = () =>
  new signalR.HubConnectionBuilder()
    .withUrl(
      `${process.env.REACT_APP_BASE_URL_CHAT}/chatHub`,
      {
        accessTokenFactory: () =>
          sharedState.accessTokenFactory?.() ||
          (typeof localStorage !== "undefined"
            ? localStorage.getItem("token") || ""
            : ""),
        withCredentials: false,
      },
    )
    .withAutomaticReconnect()
    .build();

const ensureStarted = () => {
  const connection = sharedState.connection;
  if (!connection) {
    return Promise.reject(new Error("SignalR connection is not available"));
  }

  if (
    connection.state === signalR.HubConnectionState.Connected ||
    connection.state === signalR.HubConnectionState.Reconnecting
  ) {
    return Promise.resolve(connection);
  }

  if (sharedState.startPromise) {
    return sharedState.startPromise;
  }

  sharedState.startPromise = connection
    .start()
    .then(() => connection)
    .finally(() => {
      sharedState.startPromise = null;
    });

  return sharedState.startPromise;
};

export const acquireSharedChatHubConnection = ({
  userId,
  accessTokenFactory,
}) => {
  const normalizedUserId = String(userId || "");
  if (!normalizedUserId) {
    return null;
  }

  if (
    sharedState.connection &&
    sharedState.userId &&
    sharedState.userId !== normalizedUserId
  ) {
    stopCurrentConnectionImmediately();
  }

  clearPendingStop();
  sharedState.accessTokenFactory = accessTokenFactory;

  if (!sharedState.connection) {
    sharedState.userId = normalizedUserId;
    sharedState.connection = createConnection();
  }

  sharedState.consumers += 1;
  const leasedConnection = sharedState.connection;
  let released = false;

  const release = () => {
    if (released) return;
    released = true;

    sharedState.consumers = Math.max(0, sharedState.consumers - 1);

    if (sharedState.consumers > 0) return;

    clearPendingStop();
    sharedState.stopTimer = setTimeout(() => {
      if (
        sharedState.consumers !== 0 ||
        sharedState.connection !== leasedConnection
      ) {
        return;
      }

      const connectionToStop = sharedState.connection;
      resetSharedState();
      connectionToStop?.stop().catch(() => {});
    }, STOP_GRACE_MS);
  };

  return {
    connection: leasedConnection,
    start: ensureStarted,
    release,
  };
};
